/**
 * 48 kHz 立体声 s16le（LRLR…）→ 下混单声道 → 抗混叠低通 → 8 kHz 单声道 s16le
 *
 * 下采样 6:1 前必须先滤除 > 4 kHz 的高频分量，否则会产生严重混叠失真。
 * 这里使用两级级联单极 IIR 低通（截止约 3.4 kHz），合计 −12 dB/oct 衰减，
 * 对语音频段 (300–3400 Hz) 透明且计算成本极低。
 */
export function pcmStereo48kToMono8kS16le(stereo48: ArrayBuffer): ArrayBuffer {
  const u = new Int16Array(stereo48);
  const frames = Math.floor(u.length / 2);
  if (frames < 6) {
    return new ArrayBuffer(0);
  }

  // Step 1: stereo → mono（用 Float64 做后续滤波以避免 Int16 精度损失）
  const mono48 = new Float64Array(frames);
  for (let i = 0; i < frames; i++) {
    mono48[i] = (u[i * 2] + u[i * 2 + 1]) * 0.5;
  }

  // Step 2: 抗混叠低通滤波 —— 两级级联单极 IIR, fc ≈ 3 400 Hz @ 48 kHz
  // alpha = 2π·fc·dt / (1 + 2π·fc·dt),  dt = 1/48000
  const alpha = 0.3307;
  let y1 = mono48[0];
  let y2 = mono48[0];
  for (let i = 0; i < frames; i++) {
    y1 += alpha * (mono48[i] - y1);
    y2 += alpha * (y1 - y2);
    mono48[i] = y2;
  }

  // Step 3: 6:1 抽取 + 线性插值
  const ratio = 6; // 48000 / 8000
  const outLen = Math.max(0, Math.floor(frames / ratio));
  if (outLen < 1) {
    return new ArrayBuffer(0);
  }

  const out = new Int16Array(outLen);
  for (let j = 0; j < outLen; j++) {
    const f = j * ratio;
    const i0 = Math.min(Math.floor(f), frames - 1);
    const i1 = Math.min(i0 + 1, frames - 1);
    const frac = f - Math.floor(f);
    out[j] = Math.max(-32768, Math.min(32767, Math.round(mono48[i0] + (mono48[i1] - mono48[i0]) * frac)));
  }
  return out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength);
}
